import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, ToggleLeft, ToggleRight, ArrowLeft, Menu, Copy, Shirt, Scissors, Sparkles, Wind, Droplets, Flame, Gem, Ribbon, ShoppingBag, Layers, Waves, Star, Umbrella, Sun, Zap, Award, Tag, Package, Briefcase, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { servicesAPI, sitesAPI } from '../../services/api';
import toast from 'react-hot-toast';

const SERVICE_ICONS: { name: string; icon: LucideIcon; label: string }[] = [
  { name: 'Shirt', icon: Shirt, label: 'Shirt' },
  { name: 'Scissors', icon: Scissors, label: 'Scissors' },
  { name: 'Sparkles', icon: Sparkles, label: 'Sparkles' },
  { name: 'Wind', icon: Wind, label: 'Wind' },
  { name: 'Droplets', icon: Droplets, label: 'Droplets' },
  { name: 'Flame', icon: Flame, label: 'Iron/Steam' },
  { name: 'Gem', icon: Gem, label: 'Gem' },
  { name: 'Ribbon', icon: Ribbon, label: 'Ribbon' },
  { name: 'ShoppingBag', icon: ShoppingBag, label: 'Bag' },
  { name: 'Layers', icon: Layers, label: 'Layers' },
  { name: 'Waves', icon: Waves, label: 'Waves' },
  { name: 'Star', icon: Star, label: 'Star' },
  { name: 'Umbrella', icon: Umbrella, label: 'Umbrella' },
  { name: 'Sun', icon: Sun, label: 'Sun' },
  { name: 'Zap', icon: Zap, label: 'Express' },
  { name: 'Award', icon: Award, label: 'Premium' },
  { name: 'Tag', icon: Tag, label: 'Tag' },
  { name: 'Package', icon: Package, label: 'Package' },
  { name: 'Briefcase', icon: Briefcase, label: 'Suit' },
];

const getServiceIcon = (iconName: string): LucideIcon => {
  const found = SERVICE_ICONS.find(i => i.name === iconName);
  return found ? found.icon : Shirt;
};

interface Service {
  _id: string;
  name: string;
  description: string;
  basePrice: number;
  category: string;
  code: string;
  weightUnit: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  subServices?: { _id?: string; name: string; price?: number; isActive: boolean }[];
}

interface ServiceFormData {
  name: string;
  description: string;
  basePrice: number;
  category: string;
  code: string;
  weightUnit: string;
  isActive: boolean;
  subServices: { name: string; price?: number; isActive: boolean }[];
}

interface Category {
  id: string;
  name: string;
  color: string;
  icon: string;
}

const ServiceManagement: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [sites, setSites] = useState<{ _id: string; name: string; code: string }[]>([]);
  const [selectedSourceSite, setSelectedSourceSite] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [formData, setFormData] = useState<ServiceFormData>({
    name: '',
    description: '',
    basePrice: 0,
    category: 'washing',
    code: '',
    weightUnit: 'kg',
    isActive: true,
    subServices: [],
  });

  const [categories, setCategories] = useState<Category[]>([
    { id: 'washing', name: 'Washing', color: 'blue', icon: 'Droplets' },
    { id: 'dry_cleaning', name: 'Dry Cleaning', color: 'purple', icon: 'Wind' },
    { id: 'ironing', name: 'Ironing', color: 'green', icon: 'Flame' },
    { id: 'alterations', name: 'Alterations', color: 'orange', icon: 'Scissors' },
    { id: 'special_care', name: 'Special Care', color: 'pink', icon: 'Sparkles' }
  ]);
  const [showCategorySettings, setShowCategorySettings] = useState(false);
  const [savingCategories, setSavingCategories] = useState(false);
  const units = ['kg', 'item', 'pair', 'piece'];

  const fetchCategories = async () => {
    try {
      const response = await servicesAPI.getCategories();
      const data = Array.isArray(response.data) ? response.data : [];
      if (data.length > 0) {
        setCategories(data.map((c: any) => ({ id: c.id, name: c.name, color: c.color || 'blue', icon: c.icon || 'Shirt' })));
      }
    } catch (e) {
      // Fallback to defaults on error
    }
  };

  const handleSaveCategories = async () => {
    try {
      setSavingCategories(true);
      await servicesAPI.updateCategories(categories);
      toast.success('Category icons saved successfully!');
      setShowCategorySettings(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to save categories');
    } finally {
      setSavingCategories(false);
    }
  };

  const updateCategoryIcon = (categoryId: string, icon: string) => {
    setCategories(prev => prev.map(c => c.id === categoryId ? { ...c, icon } : c));
  };

  useEffect(() => {
    fetchServices();
    fetchSites();
    fetchCategories();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await sitesAPI.getSites();
      const sitesArray = Array.isArray(response.data) ? response.data : response.data.sites || [];
      setSites(sitesArray);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const handleCopyServices = async () => {
    if (!selectedSourceSite) {
      toast.error('Please select a source site');
      return;
    }
    setIsCopying(true);
    try {
      const response = await servicesAPI.copyServicesFromSite(selectedSourceSite);
      toast.success(response.data.message);
      setShowCopyModal(false);
      setSelectedSourceSite('');
      fetchServices();
    } catch (error: any) {
      console.error('Error copying services:', error);
      toast.error(error.response?.data?.error || 'Failed to copy services');
    } finally {
      setIsCopying(false);
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.getServices();
      // Handle different response formats
      const servicesArray = Array.isArray(response.data) ? response.data : response.data.services || [];
      setServices(servicesArray);
    } catch (error: any) {
      console.error('Error fetching services:', error);
      toast.error(error.response?.data?.error || 'Failed to load services');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      if (editingService) {
        await servicesAPI.updateService(editingService._id, formData);
        toast.success('Service updated successfully!');
      } else {
        await servicesAPI.createService(formData);
        toast.success('Service created successfully!');
      }
      
      setShowModal(false);
      setEditingService(null);
      resetForm();
      fetchServices();
    } catch (error: any) {
      console.error('Error saving service:', error);
      toast.error(error.response?.data?.error || 'Failed to save service');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      basePrice: service.basePrice,
      category: service.category,
      code: service.code,
      weightUnit: service.weightUnit,
      isActive: service.isActive,
      subServices: (service.subServices || []).map(s => ({ name: s.name, price: s.price, isActive: s.isActive !== false })),
    });
    setShowModal(true);
  };

  const handleDelete = async (serviceId: string) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      setDeletingId(serviceId);
      await servicesAPI.deleteService(serviceId);
      toast.success('Service deleted successfully!');
      fetchServices();
    } catch (error: any) {
      console.error('Error deleting service:', error);
      toast.error(error.response?.data?.error || 'Failed to delete service');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleServiceStatus = async (serviceId: string, currentStatus: boolean) => {
    try {
      setTogglingId(serviceId);
      const updateData = { isActive: !currentStatus };
      await servicesAPI.updateService(serviceId, updateData);
      toast.success(`Service ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchServices();
    } catch (error: any) {
      console.error('Error updating service status:', error);
      toast.error(error.response?.data?.error || 'Failed to update service status');
    } finally {
      setTogglingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      basePrice: 0,
      category: 'Washing',
      code: '',
      weightUnit: 'kg',
      isActive: true,
      subServices: [],
    });
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  const filteredServices = (services || []).filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getCategoryName(service.category).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Admin Menu */}
        <div className="sm:flex sm:justify-between sm:items-center mb-8">
          <div className="sm:flex-auto">
            <div className="flex items-center space-x-4">
              <Link
                to="/admin"
                className="inline-flex items-center text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
              <img src="/hmpos_logo.jpeg" alt="Logo" className="h-10 w-auto rounded" />
              <div>
                <p className="mt-2 text-sm text-gray-700">Manage your laundrette services and pricing</p>
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-0 sm:ml-4 sm:flex-none">
            <div className="flex space-x-4">
              <Link
                to="/admin"
                className="px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              >
                <Menu className="h-5 w-5 mr-2" />
                Admin Menu
              </Link>
              <button
                onClick={() => setShowCategorySettings(true)}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Category Icons
              </button>
              <button
                onClick={() => setShowCopyModal(true)}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy from Site
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setEditingService(null);
                  setShowModal(true);
                }}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Service
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Services Table */}
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-md">
          {loading && services.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredServices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <div className="text-4xl mb-2">🔧</div>
                        <p className="text-lg font-medium">No services found</p>
                        <p className="text-sm">
                          {searchTerm ? 'Try adjusting your search terms' : 'Add your first service to get started'}
                        </p>
                        {!searchTerm && (
                          <button
                            onClick={() => {
                              resetForm();
                              setEditingService(null);
                              setShowModal(true);
                            }}
                            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Service
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredServices.map((service) => (
                    <tr key={service._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{service.name}</div>
                          <div className="text-sm text-gray-500">{service.code}</div>
                          {service.description && (
                            <div className="text-xs text-gray-400 mt-1">{service.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {getCategoryName(service.category)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          £{service.basePrice.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {service.weightUnit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${service.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {service.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => toggleServiceStatus(service._id, service.isActive)}
                            disabled={togglingId === service._id}
                            className={`inline-flex items-center ${service.isActive ? 'text-green-600' : 'text-gray-400'} ${togglingId === service._id ? 'opacity-50 cursor-not-allowed' : 'hover:text-opacity-75'}`}
                            title={service.isActive ? 'Deactivate Service' : 'Activate Service'}
                          >
                            {togglingId === service._id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                            ) : service.isActive ? (
                              <ToggleRight className="h-5 w-5" />
                            ) : (
                              <ToggleLeft className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEdit(service)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit Service"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(service._id)}
                            disabled={deletingId === service._id}
                            className={`text-red-600 hover:text-red-900 ${deletingId === service._id ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Delete Service"
                          >
                            {deletingId === service._id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Add/Edit Service Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingService ? 'Edit Service' : 'Add New Service'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Service Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Service Code</label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="e.g., WASH-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Price</label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-400 text-sm">£</span>
                      </div>
                      <input
                        type="number"
                        required
                        step="0.01"
                        min="0"
                        value={formData.basePrice}
                        onChange={(e) => setFormData({ ...formData, basePrice: parseFloat(e.target.value) })}
                        className="pl-8 block w-full border-gray-300 rounded-md py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Unit</label>
                    <select
                      value={formData.weightUnit}
                      onChange={(e) => setFormData({ ...formData, weightUnit: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      {units.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Sub-services</label>
                      <p className="text-xs text-gray-500">Shown as a selection popup in POS. Leave price blank to use the main service price.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, subServices: [...formData.subServices, { name: '', price: undefined, isActive: true }] })}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      + Add option
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.subServices.map((subService, index) => (
                      <div key={index} className="grid grid-cols-[1fr_110px_auto] gap-2 items-center">
                        <input
                          type="text"
                          required
                          value={subService.name}
                          onChange={(e) => setFormData({ ...formData, subServices: formData.subServices.map((s, i) => i === index ? { ...s, name: e.target.value } : s) })}
                          placeholder="e.g. Silk"
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={subService.price ?? ''}
                          onChange={(e) => setFormData({ ...formData, subServices: formData.subServices.map((s, i) => i === index ? { ...s, price: e.target.value === '' ? undefined : Number(e.target.value) } : s) })}
                          placeholder="Main price"
                          className="border border-gray-300 rounded-md px-2 py-2 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, subServices: formData.subServices.filter((_, i) => i !== index) })}
                          className="p-2 text-red-500 hover:text-red-700"
                          aria-label="Remove sub-service"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Active (available for customers)
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingService(null);
                      resetForm();
                    }}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {editingService ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>
                        {editingService ? 'Update Service' : 'Create Service'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Category Icons Modal */}
        {showCategorySettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-[600px] max-w-[95vw] max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-1">Category Icons</h3>
              <p className="text-sm text-gray-500 mb-4">Choose an icon for each service category. These icons will appear on product cards in the POS.</p>

              <div className="space-y-4">
                {categories.map((cat) => {
                  const CurrentIcon = getServiceIcon(cat.icon);
                  return (
                    <div key={cat.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <CurrentIcon className="w-5 h-5 text-gray-700" />
                        <span className="font-medium text-sm text-gray-900">{cat.name}</span>
                      </div>
                      <div className="grid grid-cols-10 gap-1.5">
                        {SERVICE_ICONS.map(({ name, icon: Icon, label }) => (
                          <button
                            key={name}
                            type="button"
                            onClick={() => updateCategoryIcon(cat.id, name)}
                            className={`flex items-center justify-center p-2 rounded-lg border-2 transition-all ${
                              cat.icon === name
                                ? 'border-primary-500 bg-primary-50 text-primary-700'
                                : 'border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700'
                            }`}
                            title={label}
                          >
                            <Icon className="w-4 h-4" />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-3 mt-5 pt-4 border-t">
                <button
                  className="px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  onClick={() => { setShowCategorySettings(false); fetchCategories(); }}
                  disabled={savingCategories}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  onClick={handleSaveCategories}
                  disabled={savingCategories}
                >
                  {savingCategories ? 'Saving...' : 'Save Icons'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Copy Services Modal */}
        {showCopyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-[480px] max-w-[95vw]">
              <h3 className="text-lg font-semibold mb-2">Copy Services from Another Site</h3>
              <p className="text-sm text-gray-500 mb-4">
                This will copy all services from the selected source site to the current site.
                Services with duplicate codes will be skipped.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Source Site</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  value={selectedSourceSite}
                  onChange={(e) => setSelectedSourceSite(e.target.value)}
                >
                  <option value="">Select a site...</option>
                  {sites.map((site) => (
                    <option key={site._id} value={site._id}>
                      {site.name} ({site.code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  onClick={() => { setShowCopyModal(false); setSelectedSourceSite(''); }}
                  disabled={isCopying}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  onClick={handleCopyServices}
                  disabled={isCopying || !selectedSourceSite}
                >
                  {isCopying ? 'Copying...' : 'Copy Services'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceManagement;
